import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page, formatBRL } from "../_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGE = {
  starter: "bg-secondary text-muted-foreground border-border",
  growth: "bg-primary/15 text-primary border-primary/30",
  pro: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

const PLAN_PRICE = { starter: 149, growth: 397, pro: 1497 };

export default function AdminSubscriptions() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/subscriptions").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const changePlan = async (uid, plan) => {
    try {
      await api.patch(`/admin/users/${uid}`, { plan });
      toast.success("Plano atualizado");
      load();
    } catch (err) {
      toast.error("Erro");
    }
  };

  if (!data) return <Skeleton className="h-96" />;

  return (
    <Page title="Assinaturas" subtitle="Gerencie planos e visualize a receita recorrente.">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <KPI icon={TrendingUp} label="MRR" value={formatBRL(data.summary.mrr)} accent />
        <KPI icon={CreditCard} label="Starter" value={data.summary.starter} hint={formatBRL(data.summary.starter * PLAN_PRICE.starter) + "/mês"} />
        <KPI icon={CreditCard} label="Growth" value={data.summary.growth} hint={formatBRL(data.summary.growth * PLAN_PRICE.growth) + "/mês"} />
        <KPI icon={CreditCard} label="Pro" value={data.summary.pro} hint={formatBRL(data.summary.pro * PLAN_PRICE.pro) + "/mês"} />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden mt-6">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Cliente</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Plano atual</TableHead>
              <TableHead className="text-right">Mensalidade</TableHead>
              <TableHead>Alterar plano</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((u) => (
              <TableRow key={u.user_id} data-testid={`sub-${u.user_id}`} className="border-border">
                <TableCell>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </TableCell>
                <TableCell className="text-muted-foreground">{u.company || "—"}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-md border ${PLAN_BADGE[u.plan]}`}>{u.plan}</span>
                </TableCell>
                <TableCell className="text-right font-mono">{u.monthly_price > 0 ? formatBRL(u.monthly_price) : "—"}</TableCell>
                <TableCell>
                  <Select value={u.plan} onValueChange={(v) => changePlan(u.user_id, v)}>
                    <SelectTrigger data-testid={`sub-select-${u.user_id}`} className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter (R$ 149)</SelectItem>
                      <SelectItem value="growth">Growth (R$ 397)</SelectItem>
                      <SelectItem value="pro">Pro (R$ 1.497)</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Page>
  );
}

const KPI = ({ icon: Icon, label, value, hint, accent }) => (
  <div className={`p-6 rounded-xl border bg-card ${accent ? "border-primary/40 glow-coral" : "border-border"}`}>
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">{label}<Icon className="w-4 h-4" /></div>
    <div className={`font-display text-3xl font-bold mt-3 ${accent ? "text-primary" : ""}`}>{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
  </div>
);
